import { Injectable } from '@angular/core';
import { ToastRef, ToastService } from '@y42/toast';
import { BehaviorSubject, distinctUntilChanged, fromEvent, map, merge, pairwise, tap } from 'rxjs';

export type ConnectionStatus = 'online' | 'offline';

@Injectable({ providedIn: 'root' })
export class ConnectionStatusService {
  constructor(private readonly toast: ToastService) {
    this.monitorConnectionStatus().subscribe();
    this.displayToastWhenOffline().subscribe();
  }

  private readonly status$$ = new BehaviorSubject<ConnectionStatus>('online');
  readonly status$ = this.status$$.asObservable().pipe(distinctUntilChanged());

  readonly online$ = this.status$.pipe(map((status) => status === 'online'));
  readonly offline$ = this.status$.pipe(map((status) => status === 'offline'));

  monitorConnectionStatus() {
    return merge(
      fromEvent(window, 'online').pipe(tap(() => this.status$$.next('online'))),
      fromEvent(window, 'offline').pipe(tap(() => this.status$$.next('offline'))),
    );
  }

  displayToastWhenOffline() {
    let toast: ToastRef;
    return this.status$.pipe(
      pairwise(),
      tap(([oldStatus, newStatus]) => {
        if (newStatus === 'offline' && !toast) {
          toast = this.toast.warning('You are offline.', { autoClose: false });
          return;
        }

        if (oldStatus === 'offline' && newStatus === 'online') {
          toast?.close();
          toast = this.toast.success('You are back online.');
          return;
        }
      }),
    );
  }
}
